import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LoginIcon from '@mui/icons-material/Login';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';

import { getKeycloakRedirectUrl } from '@/api/getKeycloakRedirectUrl.util.ts';
import bg from '@/assets/img/bg/bg1.png';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { KNOWME_EMAIL } from '@/views/contact/const/constants.ts';

import { BootstrapErrorView } from './util/getBootstrapErrorView.util';

interface Props {
  view: BootstrapErrorView;
}

export const BootstrapErrorState = ({ view }: Props) => {
  const { t } = useTranslationWithPrefix(`generalError.${view}`);
  const theme = useTheme();
  const colors = theme.colors;
  const isNoSession = view === 'noSession';
  const isServiceUnavailable = view === 'serviceUnavailable';
  const Icon =
    view === 'serviceUnavailable'
      ? CloudOffOutlinedIcon
      : view === 'notAuthorized'
        ? LockOutlinedIcon
        : PersonOffOutlinedIcon;

  const handleGoBack = () => {
    window.history.back();
  };

  const handleLogIn = () => {
    const keycloakRedirectUrl =
      window.location.origin + '/' + import.meta.env.VITE_KEYCLOAK_REDIRECT_URL;
    window.location.assign(
      getKeycloakRedirectUrl(keycloakRedirectUrl, window.location.pathname, window.location.search),
    );
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Box
      sx={{
        alignItems: 'center',
        background: `linear-gradient(90deg, ${colors.bg} 0%, rgba(5, 8, 15, 0.84) 46%, rgba(5, 8, 15, 0.58) 100%), url(${bg})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        display: 'flex',
        minHeight: '100vh',
        px: { md: 8, xs: 2 },
        py: { md: 8, xs: 4 },
        width: '100%',
      }}
    >
      <Stack
        spacing={3}
        sx={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
          boxShadow: colors.shadowCard,
          maxWidth: 620,
          p: { md: 4, xs: 3 },
          width: '100%',
        }}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Box
            sx={{
              alignItems: 'center',
              background: colors.accentBg,
              border: `1px solid ${colors.accentBorder}`,
              borderRadius: '14px',
              color: colors.accent,
              display: 'flex',
              height: 48,
              justifyContent: 'center',
              width: 48,
            }}
          >
            <Icon />
          </Box>
          <Typography
            sx={{
              color: colors.accent,
              fontSize: 13,
              fontWeight: 800,
              textTransform: 'uppercase',
            }}
          >
            {t('statusLabel')}
          </Typography>
        </Stack>

        <Stack spacing={1.5}>
          <Typography
            sx={{
              color: colors.textPrimary,
              fontSize: { md: 34, xs: 28 },
              fontWeight: 800,
              lineHeight: 1.15,
            }}
          >
            {t('message')}
          </Typography>
          <Typography
            sx={{
              color: colors.textSecondary,
              fontSize: 16,
              lineHeight: 1.65,
              maxWidth: 520,
            }}
          >
            {t('subMessage')}
          </Typography>
          {isServiceUnavailable && (
            <Typography
              component="a"
              href={`mailto:${KNOWME_EMAIL}`}
              sx={{
                color: colors.accent,
                fontSize: 16,
                fontWeight: 700,
                textDecoration: 'none',
                width: 'fit-content',
              }}
            >
              {KNOWME_EMAIL}
            </Typography>
          )}
        </Stack>

        <Stack direction={{ md: 'row', xs: 'column' }} spacing={1.5} sx={{ pt: 1 }}>
          {isServiceUnavailable ? (
            <Button onClick={handleReload} startIcon={<RefreshIcon />} variant="contained">
              {t('primaryAction')}
            </Button>
          ) : isNoSession ? (
            <Button onClick={handleLogIn} startIcon={<LoginIcon />} variant="contained">
              {t('primaryAction')}
            </Button>
          ) : (
            <Button onClick={handleGoBack} startIcon={<ArrowBackIcon />} variant="outlined">
              {t('primaryAction')}
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
